/**
 * src/shell/auth/services/StepUpChallengeBus.ts
 *
 * Decoupled Observer-Based Step-Up MFA Challenge Bus (RFC 9470).
 * Prevents circular React context dependencies between AuthProvider and ModalStackProvider.
 */

import { EventEmitter } from '../../utils/event-emitter';

export interface StepUpRequest {
  id: string;
  capability: string;
  reason: string;
  scope: string;
  requestedAt: number;
}

export interface StepUpResult {
  requestId: string;
  elevatedToken: string;
  capability: string;
  expiresAt: number;
}

type ChallengeCallback = (req: StepUpRequest, resolve: (result: StepUpResult) => void, reject: (err: Error) => void) => void;

export class StepUpChallengeBus extends EventEmitter {
  private static instance: StepUpChallengeBus;
  private handler: ChallengeCallback | null = null;
  private elevatedClaims: Map<string, StepUpResult> = new Map();

  private constructor() {
    super();
  }

  public static getInstance(): StepUpChallengeBus {
    if (!StepUpChallengeBus.instance) {
      StepUpChallengeBus.instance = new StepUpChallengeBus();
    }
    return StepUpChallengeBus.instance;
  }

  /**
   * Registers the active StepUpChallengeModal handler.
   */
  public registerHandler(cb: ChallengeCallback): () => void {
    this.handler = cb;
    return () => {
      if (this.handler === cb) {
        this.handler = null;
      }
    };
  }

  /**
   * Requests step-up elevation for a privileged capability.
   */
  public async requestElevation(capability: string, reason = 'Privileged capability access required', scope = 'step-up'): Promise<StepUpResult> {
    // Check if we already have a valid unexpired elevated claim for this capability
    const existing = this.elevatedClaims.get(capability);
    if (existing && Date.now() < existing.expiresAt) {
      return existing;
    }

    if (!this.handler) {
      throw new Error(`[StepUpChallengeBus] No Step-Up challenge UI handler mounted for capability: ${capability}`);
    }

    const request: StepUpRequest = {
      id: 'stepup_' + Math.random().toString(36).substring(2, 10),
      capability,
      reason,
      scope,
      requestedAt: Date.now(),
    };

    return new Promise((resolve, reject) => {
      this.handler!(
        request,
        (res) => {
          this.elevatedClaims.set(capability, res);
          this.emit('stepup:success', res);
          resolve(res);
        },
        (err) => {
          this.emit('stepup:failed', { request, error: err.message });
          reject(err);
        },
      );
    });
  }

  /**
   * Checks if an unexpired elevated claim exists.
   */
  public hasElevatedClaim(capability: string): boolean {
    const claim = this.elevatedClaims.get(capability);
    return !!claim && Date.now() < claim.expiresAt;
  }

  /**
   * Revokes all elevated claims immediately.
   */
  public clearElevatedClaims(): void {
    this.elevatedClaims.clear();
  }
}

export const stepUpChallengeBus = StepUpChallengeBus.getInstance();
